/* eslint-disable max-len */
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { DocMetaData, Heading as HeadingType } from 'src/docs/types';

export interface LinkHash {
  visible: boolean;
  top: number;
  left: number;
}

const defaultAppbarHeight = 48;

interface Props {
  children: Record<string, any>;
  level: number;
  meta: DocMetaData;
}

function flatten(text: string, child: any): any {
  return typeof child === 'string' ? text + child : React.Children.toArray(child.props.children).reduce(flatten, text);
}

function BaseHeading(props: Props): React.ReactElement {
  const { meta, level } = props;
  const theme = useTheme();
  const children = React.Children.toArray(props.children);
  const text = children.reduce(flatten, '').trim();
  const heading = meta.headings.find((h: HeadingType) => h.title === text);
  const offset = (typeof theme.mixins.toolbar.minHeight === 'number' ? theme.mixins.toolbar.minHeight : defaultAppbarHeight) + Number(theme.spacing(2).split('px')[0]);

  return React.createElement(
    `h${level}`,
    {
      id: heading?.slug,
      style: {
        paddingTop: offset * 2,
        marginTop: -offset,
        marginBottom: offset / 2,
        '& .anchor-link-style': {
          display: 'none',
        },
        '&:hover .anchor-link-style': {
          display: 'inline-block',
          padding: '0 8px',
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.text.primary,
          },
          '& svg': {
            width: '0.7em',
            height: '0.7em',
            fill: 'currentColor',
          },
        },
      },
      className: 'anchor-link-style',
    },
    text,
    <a className="anchor-link-style" aria-hidden="true" aria-label="anchor" href={`#${heading?.slug}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
        <path d="M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z" />
      </svg>
    </a>,
  );
}

function withSubscription(WrappedComponent: any, meta: DocMetaData): React.ComponentClass {
  return class LeveledHeading extends React.Component {
    render(): React.ReactElement {
      return <WrappedComponent {...this.props} meta={meta} />;
    }
  };
}

const Heading = (meta: DocMetaData) => withSubscription(BaseHeading, meta);

export default Heading;
